import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { SimpleTooltip } from '../../ui/Tooltip/Tooltip';
import { Spinner } from '../../ui/Spinner/Spinner';
import { canvasService } from '../../../services/canvasService';
import { MessageSquare, Sparkles, Lightbulb, X } from 'lucide-react';
import { useNodeResize } from './useNodeResize';

interface PromptNodeDataProps {
  text?: string;
  aiGenerated?: boolean;
  canvasId?: string;
  nodeWidth?: number;
  onUpdate?: (id: string, updates: any) => void;
  onDelete?: (id: string) => void;
  onWidthChange?: (id: string, width: number) => void;
}

export function PromptNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as PromptNodeDataProps;
  const [text, setText] = useState(data.text || '');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isBrainstorming, setIsBrainstorming] = useState(false);
  const isProcessing = isEnhancing || isBrainstorming;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth as number | undefined,
    minWidth: 200,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  // Sync external text changes (e.g. from AI enhance/brainstorm or WebSocket)
  useEffect(() => {
    if (data.text !== undefined && data.text !== text) {
      setText(data.text);
    }
  }, [data.text]);

  // Auto-resize textarea to fit content
  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [text, nodeWidth, autoResize]);

  const saveText = useCallback((value: string) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (data.onUpdate) {
        data.onUpdate(id, { data: { ...data, text: value } });
      }
    }, 500);
  }, [id, data]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setText(value);
    saveText(value);
  }, [saveText]);

  // Save immediately on blur (in case the debounce hasn't fired yet)
  const handleBlur = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (data.onUpdate) {
      data.onUpdate(id, { data: { ...data, text } });
    }
  }, [id, text, data]);

  const handleEnhance = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text.trim() || isProcessing || !data.canvasId) return;
    setIsEnhancing(true);
    try {
      await canvasService.enhancePrompt(data.canvasId, id);
    } catch (err) {
      console.error('Enhance failed:', err);
    } finally {
      setIsEnhancing(false);
    }
  }, [id, text, data.canvasId, isProcessing]);

  const handleBrainstorm = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text.trim() || isProcessing || !data.canvasId) return;
    setIsBrainstorming(true);
    try {
      await canvasService.brainstormPrompt(data.canvasId, id);
    } catch (err) {
      console.error('Brainstorm failed:', err);
    } finally {
      setIsBrainstorming(false);
    }
  }, [id, text, data.canvasId, isProcessing]);

  return (
    <div className={`bg-white rounded-lg border-2 shadow-md min-w-[200px] relative ${!nodeWidth ? 'max-w-[300px]' : ''} ${selected ? 'border-primary-500' : 'border-gray-200'}`} style={nodeWidth ? { width: nodeWidth } : undefined}>
      <div className="px-3 py-2 bg-primary-50 border-b border-primary-100 rounded-t-lg flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-primary-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-primary-700">Prompt</span>
        {data.aiGenerated && (
          <span className="text-[10px] px-1.5 py-px rounded-md leading-none bg-violet-100 text-violet-600 font-semibold">AI</span>
        )}

        {/* AI action buttons */}
        {text.trim() && (
          <div className="ml-auto flex items-center gap-0.5">
            <SimpleTooltip content="Enhance" side="top">
              <button
                onClick={handleEnhance}
                disabled={isProcessing}
                className="text-gray-400 hover:text-violet-600 p-0.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Enhance prompt"
              >
                {isEnhancing ? (
                  <Spinner size="xs" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
              </button>
            </SimpleTooltip>
            <SimpleTooltip content="Brainstorm" side="top">
              <button
                onClick={handleBrainstorm}
                disabled={isProcessing}
                className="text-gray-400 hover:text-violet-600 p-0.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Brainstorm variations"
              >
                {isBrainstorming ? (
                  <Spinner size="xs" />
                ) : (
                  <Lightbulb className="w-3.5 h-3.5" />
                )}
              </button>
            </SimpleTooltip>
            {selected && (
              <button
                onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
                className="text-gray-400 hover:text-red-500 p-0.5 rounded transition-colors"
                aria-label="Delete node"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Delete button when no text (fallback) */}
        {!text.trim() && selected && (
          <button
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
            className="ml-auto text-gray-400 hover:text-red-500 p-0.5"
            aria-label="Delete node"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-3">
        <textarea
          ref={textareaRef}
          className="w-full min-h-[60px] text-sm border border-gray-200 rounded-md p-2 resize-none overflow-hidden focus:outline-none focus:ring-1 focus:ring-primary-300 focus:border-primary-400 nodrag nopan nowheel"
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Describe the image you want to generate..."
        />
      </div>
      {/* Prompt nodes only output, they don't receive input */}
      <Handle type="source" position={Position.Right} className="!bg-primary-500 !w-3 !h-3" />
      <div {...resizeHandleProps} />
    </div>
  );
}
